import { Image as ImageIcon, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { photoSrc } from "@/lib/api";

export const inputCls =
  "min-w-0 flex-1 rounded-lg border border-gold/40 bg-white px-3 py-2 text-sm outline-none transition-all focus:border-gold focus:ring-2 focus:ring-gold/25";

export const labelCls = "mb-1 block text-xs font-semibold uppercase tracking-wider text-wine/60";

export const newKey = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));

export const withKeys = (rows) => rows.map((r) => (r._key ? r : { ...r, _key: newKey() }));

export const stripKeys = (rows) => rows.map(({ _key, ...r }) => r);

export function Section({ title, hint, section, saving, onSave, children }) {
  return (
    <div className="rounded-3xl border border-gold/40 bg-white/85 p-5 shadow-sm backdrop-blur sm:p-7" data-testid={`content-section-${section}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-serif text-xl font-semibold text-burgundy">{title}</h3>
          {hint && <p className="mt-1 text-xs text-plum/55">{hint}</p>}
        </div>
        <button
          onClick={() => onSave(section)}
          disabled={saving !== ""}
          data-testid={`content-save-${section}`}
          className="flex shrink-0 items-center gap-1.5 rounded-full border border-gold bg-gold px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white transition-all hover:bg-golddeep disabled:opacity-50"
        >
          {saving === section ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save
        </button>
      </div>
      <div className="mt-5">{children}</div>
    </div>
  );
}

export function RowsEditor({ rows, fields, onChange, addLabel, testidPrefix }) {
  return (
    <div className="space-y-2">
      {rows.map((row, i) => (
        <div key={row._key} className="flex items-center gap-2">
          {fields.map((f) => (
            <input
              key={f.key}
              value={row[f.key]}
              placeholder={f.placeholder}
              onChange={(e) => onChange(rows.map((r) => (r._key === row._key ? { ...r, [f.key]: e.target.value } : r)))}
              data-testid={`${testidPrefix}-${f.key}-${i}`}
              className={inputCls}
            />
          ))}
          <button
            onClick={() => onChange(rows.filter((r) => r._key !== row._key))}
            aria-label="Delete row"
            data-testid={`${testidPrefix}-delete-${i}`}
            className="shrink-0 rounded-lg p-2 text-plum/40 transition-colors hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button
        onClick={() => onChange([...rows, { ...Object.fromEntries(fields.map((f) => [f.key, ""])), _key: newKey() }])}
        data-testid={`${testidPrefix}-add`}
        className="flex items-center gap-1.5 rounded-full border border-dashed border-gold/60 px-4 py-1.5 text-xs font-medium text-golddeep transition-colors hover:bg-rose/60"
      >
        <Plus className="h-3.5 w-3.5" /> {addLabel}
      </button>
    </div>
  );
}

export function PhotoPicker({ photos, value, onChange, testId }) {
  const chosen = photos.find((p) => p.id === value);
  return (
    <div className="flex items-center gap-2">
      {chosen ? (
        <img src={photoSrc(chosen.url)} alt="" className="h-10 w-10 shrink-0 rounded-lg border border-gold/40 object-cover" data-testid={`${testId}-thumb`} />
      ) : (
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-dashed border-gold/50 text-plum/30">
          <ImageIcon className="h-4 w-4" />
        </div>
      )}
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className={`${inputCls} text-xs`}>
        <option value="">No linked cake photo</option>
        {photos.map((p) => (
          <option key={p.id} value={p.id}>
            {p.albumName} — {p.caption || (p.tags || []).slice(0, 3).join(", ") || "untitled"}
          </option>
        ))}
      </select>
    </div>
  );
}
